import { useMutation, useQuery } from '@apollo/client/react';
import {
  DECIDE_DUNNING_REVIEW,
  REACTIVATE_DUNNING_ACCOUNT,
  DUNNING_STRIPE_EXCLUSIONS,
  DUNNING_REVIEW_QUEUE,
  DUNNING_SUSPENDED_LIST,
  DUNNING_SWEEP_ABORTS,
} from '@staff-app/graphql-client';
import type {
  DunningReviewRow,
  StripeDunningExclusionRow,
} from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';

/**
 * May this staffer see and decide dunning reviews?
 *
 * ROOT or BILLING (since 2026-09-23 — it replaced the manager email allowlist). Decided on
 * the SERVER (`StaffService.dunningReviewCanDecide`) and read here as a flag, so the web
 * never re-derives the rule and cannot drift from what the resolvers enforce.
 *
 * Reads the flag off `staffMe`, which the shell already fetches once, rather than
 * issuing a query of its own. The sidebar gates on this, and a sidebar that makes
 * a network call is one every future test has to wrap in an Apollo provider —
 * which is exactly what broke `StaffSidebar.test.tsx` when this first shipped as
 * its own query.
 *
 * FALSE until staffMe has settled, and false on error. The entry appearing and
 * then vanishing is worse than appearing a beat late, and a failure must never
 * open a door onto the queue of customers who are one decision from going dark.
 */
export function useDunningReviewAccess(): { canDecide: boolean; loading: boolean } {
  const adminUser = useAuthStore((s) => s.adminUser);
  const loaded = useAuthStore((s) => s.adminUserLoaded);
  return { canDecide: adminUser?.canDecideDunningReview === true, loading: !loaded };
}

/**
 * The review queue, oldest wait first.
 *
 * `cache-and-network` because a manager typically arrives here from a Slack nudge
 * about one specific account: a cached list is fine to paint immediately, but it
 * must not be what they act on.
 */
export function useDunningReviewQueue(): {
  rows: DunningReviewRow[];
  loading: boolean;
  error?: Error;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useQuery<{
    dunningReviewQueue: DunningReviewRow[];
  }>(DUNNING_REVIEW_QUEUE, { fetchPolicy: 'cache-and-network' });

  return {
    rows: data?.dunningReviewQueue ?? [],
    loading,
    error: error as Error | undefined,
    refetch: () => void refetch(),
  };
}

/** One sweep that is refusing to run, as the banner renders it. */
export interface DunningSweepAbort {
  job: string;
  candidates: number;
  cap: number;
  detail?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  ticks: number;
}

/**
 * The sweeps refusing to run right now.
 *
 * `network-only`: this answers "is the engine stopped RIGHT NOW", and a cached yes-or-no is worse
 * than no answer. Polls on the same page the manager is already watching, so an abort that starts
 * while they have it open still surfaces.
 */
export function useDunningSweepAborts(): { aborts: DunningSweepAbort[]; loading: boolean } {
  const { data, loading } = useQuery<{ dunningSweepAborts: DunningSweepAbort[] }>(
    DUNNING_SWEEP_ABORTS,
    { fetchPolicy: 'network-only', pollInterval: 60_000 },
  );
  return { aborts: data?.dunningSweepAborts ?? [], loading };
}

/**
 * Accounts whose screens are already off — the reversal list.
 *
 * Separate query rather than a filter on the queue: the two answer different
 * questions (work outstanding vs work done and still undoable), and merging them
 * would make the queue look permanently full.
 */
export function useDunningSuspendedList(): {
  rows: DunningReviewRow[];
  loading: boolean;
} {
  const { data, loading } = useQuery<{ dunningSuspendedList: DunningReviewRow[] }>(
    DUNNING_SUSPENDED_LIST,
    { fetchPolicy: 'cache-and-network' },
  );
  return { rows: data?.dunningSuspendedList ?? [], loading };
}

/**
 * Apply a manager's decision to one review.
 *
 * Refetches the queue on success rather than mutating the cache by hand: the
 * decided row leaves the list entirely (it is no longer at `review`), and a
 * hand-patched cache would need to know that. It also re-reads whether anything
 * else has landed while the manager was reading.
 */
export function useDecideDunningReview(): {
  decide: (accountId: string, reason: string) => Promise<boolean>;
  deciding: boolean;
} {
  const [mutate, { loading }] = useMutation(DECIDE_DUNNING_REVIEW, {
    // Both lists: a decision moves a row from one to the other, so refetching
    // only the queue would leave the suspended section stale.
    refetchQueries: [{ query: DUNNING_REVIEW_QUEUE }, { query: DUNNING_SUSPENDED_LIST }],
    awaitRefetchQueries: true,
  });

  const decide = async (accountId: string, reason: string): Promise<boolean> => {
    const res = (await mutate({ variables: { accountId, reason } })) as {
      data?: { decideDunningReview?: { applied?: boolean } | null } | null;
    };
    return res?.data?.decideDunningReview?.applied === true;
  };

  return { decide, deciding: loading };
}

/**
 * Customers excluded IN STRIPE from the Billing Console (`staff-app-dunning-exclusion` =
 * 'true' or 'pause'). A live Stripe search on the server, so `network-only`: a cached list
 * would show a pause that has since ended. `search` matches email, domain or Stripe id.
 */
export function useDunningStripeExclusions(search: string): {
  rows: StripeDunningExclusionRow[];
  loading: boolean;
  error?: Error;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useQuery<{
    dunningStripeExclusions: StripeDunningExclusionRow[];
  }>(DUNNING_STRIPE_EXCLUSIONS, {
    variables: { search: search.trim() || null },
    fetchPolicy: 'network-only',
  });
  return {
    rows: data?.dunningStripeExclusions ?? [],
    loading,
    error: error as Error | undefined,
    refetch: () => void refetch(),
  };
}

/**
 * Reactivate a deactivated account — screens + portal back, and it returns to awaiting a
 * decision (the invoice is still unpaid). Refetches both lists: the row changes stage.
 */
/** What Reactivate did — `message` says whether the Force Refresh screen push landed. */
export interface ReactivateOutcome {
  applied: boolean;
  screenPush?: string | null;
  message?: string | null;
}

export function useReactivateDunningAccount(): {
  reactivate: (accountId: string, reason: string) => Promise<ReactivateOutcome>;
  reactivating: boolean;
} {
  const [mutate, { loading }] = useMutation(REACTIVATE_DUNNING_ACCOUNT, {
    refetchQueries: [{ query: DUNNING_REVIEW_QUEUE }, { query: DUNNING_SUSPENDED_LIST }],
    awaitRefetchQueries: true,
  });
  const reactivate = async (accountId: string, reason: string): Promise<ReactivateOutcome> => {
    const res = (await mutate({ variables: { accountId, reason } })) as {
      data?: { reactivateDunningAccount?: Partial<ReactivateOutcome> | null } | null;
    };
    const out = res?.data?.reactivateDunningAccount;
    return { applied: out?.applied === true, screenPush: out?.screenPush, message: out?.message };
  };
  return { reactivate, reactivating: loading };
}
